import React, { useEffect, useState } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { panelPath } from '../config/paths';
import LanguageSwitcher from '../components/LanguageSwitcher';
import ScrollProgressBar from '../components/ScrollProgressBar';

function LogoMark({ size = 26 }: { size?: number }) {
  return (
    <div
      className="flex shrink-0 items-center justify-center rounded-md bg-lc-pink font-bold text-lc-bg"
      style={{ width: size, height: size, fontSize: size * 0.54 }}
    >
      L
    </div>
  );
}

export default function AppLayout() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-lc-bg text-lc-text overflow-x-hidden">
      <header
        className={`sticky top-0 z-50 border-b border-lc-border transition-[background-color,backdrop-filter] duration-300 ${
          scrolled ? 'bg-lc-bg/95 backdrop-blur-md' : 'bg-lc-bg/90 backdrop-blur-sm'
        }`}
      >
        <div className="mx-auto flex max-w-[1180px] items-center justify-between px-6 py-[18px]">
          <Link to="/" className="flex items-center gap-2">
            <LogoMark />
            <span className="text-lg font-bold tracking-tight">Loopcast</span>
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-sm text-lc-soft">
            <a href="#how" className="hover:text-lc-pink transition-colors">{t('appLayout.how')}</a>
            <a href="#features" className="hover:text-lc-pink transition-colors">{t('appLayout.features')}</a>
            <a href="#pricing" className="hover:text-lc-pink transition-colors">{t('appLayout.pricing')}</a>
            <a href="#faq" className="hover:text-lc-pink transition-colors">{t('appLayout.faq')}</a>
          </nav>
          <div className="flex items-center gap-3.5">
            <LanguageSwitcher variant="dark" />
            {user ? (
              <Link to={panelPath('/')} className="btn-lc text-[13.5px] px-[18px] py-2.5">
                {t('appLayout.goToPanel')}
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-[13.5px] font-medium text-lc-soft hover:text-lc-pink transition-colors whitespace-nowrap"
                >
                  {t('appLayout.login')}
                </Link>
                <Link to="/register" className="btn-lc text-[13.5px] px-[18px] py-2.5 whitespace-nowrap">
                  {t('appLayout.registerFree')}
                </Link>
              </>
            )}
          </div>
        </div>
        <ScrollProgressBar />
      </header>

      <main className="flex-1" id="main-content">
        <Outlet />
      </main>

      <footer className="border-t border-lc-border px-6 pb-10 pt-14">
        <div className="mx-auto grid max-w-[1180px] grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-[2fr_1fr_1fr_1fr]">
          <div>
            <div className="mb-3.5 flex items-center gap-2">
              <LogoMark size={22} />
              <span className="text-[15px] font-bold">Loopcast</span>
            </div>
            <p className="max-w-[280px] text-[13.5px] leading-relaxed text-lc-faint">{t('appLayout.footerDesc')}</p>
          </div>
          <div>
            <div className="mb-4 text-xs font-semibold uppercase tracking-wide text-lc-faint">
              {t('appLayout.footerProduct')}
            </div>
            <div className="flex flex-col gap-2.5 text-[13.5px]">
              <a href="#how" className="text-lc-muted hover:text-lc-pink transition-colors">{t('appLayout.how')}</a>
              <a href="#features" className="text-lc-muted hover:text-lc-pink transition-colors">{t('appLayout.features')}</a>
              <a href="#pricing" className="text-lc-muted hover:text-lc-pink transition-colors">{t('appLayout.pricing')}</a>
            </div>
          </div>
          <div>
            <div className="mb-4 text-xs font-semibold uppercase tracking-wide text-lc-faint">
              {t('appLayout.footerContact')}
            </div>
            <div className="flex flex-col gap-2.5 text-[13.5px]">
              <a href="mailto:merhaba@loopcast.io" className="text-lc-muted hover:text-lc-pink transition-colors">
                merhaba@loopcast.io
              </a>
              <a
                href="https://instagram.com/prompt.overflowed"
                target="_blank"
                rel="noreferrer"
                className="text-lc-muted hover:text-lc-pink transition-colors"
              >
                @prompt.overflowed
              </a>
            </div>
          </div>
          <div>
            <div className="mb-4 text-xs font-semibold uppercase tracking-wide text-lc-faint">
              {t('appLayout.footerLegal')}
            </div>
            <div className="flex flex-col gap-2.5 text-[13.5px]">
              <span className="text-lc-muted">{t('appLayout.legalPrivacy')}</span>
              <span className="text-lc-muted">{t('appLayout.legalTerms')}</span>
            </div>
          </div>
        </div>
        <div className="mx-auto mt-10 max-w-[1180px] border-t border-lc-border pt-6 text-[12.5px] text-lc-faint">
          {t('appLayout.footerRights', { year: new Date().getFullYear() })}
        </div>
      </footer>
    </div>
  );
}
